import { usePrintPreview } from '@/features/print/hooks/usePrint';
import { eposXmlToLines, type PreviewLine } from '@/features/print/lib/epos-preview';
import { useT } from '@/i18n';
import type { PrintPreviewKind } from '@restaurant/api-client';
import { Preloader } from 'framework7-react';

// LARGE scales every line (body included), so sizes are shown RELATIVE to the
// narrowest line — otherwise body text would overflow the `columns`-wide paper.
function baseWidth(lines: PreviewLine[]): number {
  const min = lines.reduce((acc, l) => Math.min(acc, l.width), Number.POSITIVE_INFINITY);
  return Number.isFinite(min) && min > 0 ? min : 1;
}

/** A paper-strip rendering of the sample ticket for the given kind. */
export function TicketPreview({ kind }: { kind: PrintPreviewKind }) {
  const t = useT();
  const { data, isLoading, isError } = usePrintPreview(kind);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 24 }}>
        <Preloader />
      </div>
    );
  }

  if (isError || !data) {
    return <p style={{ color: 'var(--f7-text-color-red, #ff3b30)' }}>{t.print.previewError}</p>;
  }

  const lines = eposXmlToLines(data.eposXml);
  const base = baseWidth(lines);

  return (
    <div style={{ overflowX: 'auto' }}>
      <div
        style={{
          width: `${data.columns}ch`,
          padding: 12,
          margin: '0 auto',
          background: '#fff',
          color: '#000',
          borderRadius: 4,
          boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
          fontFamily: 'ui-monospace, Menlo, monospace',
          fontSize: 12,
          lineHeight: 1.35,
          whiteSpace: 'pre',
        }}
      >
        {lines.map((line, i) => {
          // Width drives the glyph size (and so the column count); a taller-than-
          // wide run is stretched vertically only, like the printer does.
          const w = line.width / base;
          const stretch = line.height / line.width;
          return (
            <div
              key={i}
              style={{
                textAlign: line.align,
                fontSize: w > 1 ? `${w}em` : undefined,
                lineHeight: w > 1 || stretch > 1 ? 1.1 * stretch : undefined,
                transform: stretch > 1 ? `scaleY(${stretch})` : undefined,
                transformOrigin: 'center',
              }}
            >
              {line.segments.length === 0
                ? ' '
                : line.segments.map((s, j) =>
                    s.bold ? <b key={j}>{s.text}</b> : <span key={j}>{s.text}</span>,
                  )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
